import { ConfirmModal } from "@components/ConfirmModal";
import { FullPicker } from "@components/ImagePicker";
import { Loading } from "@components/Loading";
import { TopNav } from "@components/TopNav";
import { convertToPng } from "@helpers/compress";
import { FirebaseContext } from "@helpers/firebase";
import { useToast } from "@helpers/useToast";
import { useTranslation } from "@helpers/useTranslation";
import { Timestamp, doc, setDoc } from "firebase/firestore";
import { deleteObject, ref as storageRef, uploadBytes } from "firebase/storage";
import { useContext, useEffect, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";

export const MyBranding = () => {
  const { t } = useTranslation("admin");
  const { functions, firestore, storage } = useContext(FirebaseContext);
  const [getMyEvents, loading] = useHttpsCallable<API.GetMyEventsRequest, API.GetMyEventsResponse>(
    functions,
    "getMyEventsV2",
  );
  const [myEvents, setMyEvents] = useState<MyEvent[]>([]);

  const [saving, setSaving] = useState(false);
  const [image, setImage] = useState(undefined);

  useEffect(() => {
    getMyEvents().then((result) => {
      setMyEvents(result.data);
    });
  }, []);

  const toast = useToast();
  const onSubmit = async () => {
    setSaving(true);
    try {
      if (image) {
        // The backend pipeline deliberately skips `branding` files and serves them raw,
        // so normalise here: downscale + re-encode as PNG (keeps logo transparency).
        const logo = await convertToPng(image);
        for (const event of myEvents) {
          const eventId = event.id;
          const imagePath = `events/${eventId}/branding`;
          const imageRef = storageRef(storage, imagePath);
          await uploadBytes(imageRef, logo);

          // Set boolean flag that this event has branding (for the frontend to know to show the branding instead of the default Camdeed/Tuokis logo)
          const docRef = doc(firestore, `events/${eventId}`);
          await setDoc(docRef, { hasBranding: true, lastModifiedOn: Timestamp.now() }, { merge: true });
        }
      }
      toast(t("updated"), "👍", "success", 1500);
    } catch (error) {
      console.error(error);
      toast(t("problemOccured"), "😢", "error");
    } finally {
      setSaving(false);
    }
  };

  const onDeleteBranding = async () => {
    setSaving(true);
    let failed = false;
    for (const event of myEvents) {
      const eventId = event.id;
      try {
        const imagePath = `events/${eventId}/branding`;
        const imageRef = storageRef(storage, imagePath);
        await deleteObject(imageRef);
      } catch (error) {
        // The file may already be gone (e.g. deleted via console); only real failures matter
        if (error.code !== "storage/object-not-found") {
          console.error(error);
          failed = true;
        }
      }

      try {
        // The flag is what controls rendering, so always clear it even if the file was missing
        const docRef = doc(firestore, `events/${eventId}`);
        await setDoc(docRef, { hasBranding: false, lastModifiedOn: Timestamp.now() }, { merge: true });
      } catch (error) {
        console.error(error);
        failed = true;
      }
    }
    setSaving(false);
    if (failed) toast(t("problemOccured"), "😢", "error");
    else toast(t("deleted"), "👍", "success");
  };
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  if (!myEvents.length)
    return (
      <>
        <TopNav isAdmin={true} useAllLocales showBack />
        <div className="flex-1 flex-col">
          <div className="mb-24 flex flex-col items-center py-4 px-6">
            <h1 className="mt-12 mb-8 text-center text-2xl font-semibold">{t("noEventsAvailable")}</h1>
          </div>
        </div>
      </>
    );

  // Any one of the events will do, as they all have the same branding
  const eventId = myEvents[0].id;

  return (
    <>
      <TopNav isAdmin={true} useAllLocales showBack />
      <ConfirmModal
        messageCopy={t("deleteBrandingMessage")}
        show={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={onDeleteBranding}
      />
      <main className="flex-1 flex-col">
        <div className="mb-24 flex flex-col items-center py-4 px-6">
          <Loading loading={loading}>
            <div className="flex w-full max-w-lg flex-col">
              <h1 className="mt-12 mb-8 text-center text-4xl font-semibold">{t("myBranding")}</h1>
              <p className="mb-12 text-center text-lg font-light">{t("aboutMyBranding")}</p>
              <Loading loading={saving}>
                <FullPicker
                  defaultImageUri={`events/${eventId}/branding`}
                  getFreshDefault={true}
                  uploadLabel={t("upload")}
                  reuploadLabel={t("reupload")}
                  onNewImage={setImage}
                  allowUpload
                />
                <div className="flex max-w-lg justify-center">
                  <div className="mb-12 mt-8">
                    <button
                      onClick={onSubmit}
                      type="button"
                      disabled={saving}
                      className={`inline-flex w-full justify-center rounded-md bg-primary px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-base-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 ${
                        saving ? "loading" : ""
                      }`}
                      id="submit-button"
                    >
                      {t("save")}
                    </button>
                    <div className="mt-8 flex justify-center">
                      <button
                        onClick={() => setShowConfirmModal(true)}
                        type="button"
                        disabled={saving}
                        className={`${saving ? "loading" : ""} btn-outline btn-error btn`}
                      >
                        {t("delete")}
                      </button>
                    </div>
                  </div>
                </div>
              </Loading>
            </div>
          </Loading>
        </div>
      </main>
    </>
  );
};
